import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setStaffInfo } from 'redux/actions'
import SubHeader from 'components/SubHeader';
import ModalResetPassword from 'screens/ModalResetPassword';
import ModalRemoveStaff from 'screens/ModalRemoveStaff';
import FooterBar from 'components/FooterBar';
import Spinner from 'components/Spinner';
import Button from 'components/Button';
import StaffInformation from './components/StaffInformation';
import StaffInformationImages from './components/StaffInformationImages';
import ModalStaffModifyAccess from 'screens/ModalStaffModifyAccess';
import ModalGenerateKey from 'screens/ModalCreateKey';
import MetaInformation from './components/MetaInformation';
import GeneralStaffNotes from './components/GeneralStaffNotes';
//import WorkSchedule from './components/WorkSchedule';
import SendStaffMessage from './components/SendStaffMessage';
import CommunicationHistory from './components/CommunicationHistory';
import { useQuery } from '@apollo/client';
import { GET_STAFF_INFO_BY_STAFF_ID } from 'graphql/service';
import { useStaffForm } from 'customHook/useStaffForm';
import {
  Content,
  ContentColumn,
  Container,
  DesktopCommunicationContainer,
  MobileCommunicationContainer
} from 'components/BookingMain/styles';
import ChatIcon from 'assets/icons/chat.svg'
import { debouncedErrorMessage } from 'utils/messageUtil';

const StaffDetails = () => {

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const staffId= useParams().staffId

  const staffInfo = useSelector(state => state.staffInfoReducer.staffInfo)
  const { updateStaffInfo, updateUserInfoLoading, staffInfoError, setStaffInfoError  } = useStaffForm()

  //staffInformation
  const [deviceId, setDeviceId] = useState('Kiosk 1')
  const [KeyPrintModalIsOpen, setKeyPrintModalIsOpen] = useState(false)

  //GeneralStaffNotes
  const [staffNotes, setStaffNotes] = useState([])

  const { data: staffInfoData, loading, error } = useQuery(
    GET_STAFF_INFO_BY_STAFF_ID,
    {
      variables: { staffId: staffId }
    }
  )

  useEffect(() => {
    const staff = staffInfoData?.staffByID;
    if (!staff) return;

    const newStaff = {
      ...staffInfo,
      staffId,
      fullName: staff.user?.fullName ?? '',
      email: staff.user?.email ?? '',
      phoneNumber: staff.user?.phoneNumber ?? '',
      streetAddress: staff.user?.address ?? '',
      streetAddressLine2: staff.user?.addressLine2 ?? '',
      city: staff.user?.city ?? '',
      province: staff.user?.province ?? '',
      postalCode: staff.user?.postalCode ?? '',
      country: staff.user?.country ?? '',
      staffRoleIds: [staff.roles?.[0]?.roleId],
      isActive: staff.isActive ?? false,
    };
    dispatch(setStaffInfo(newStaff));
    setStaffNotes(staff.staffNotes ?? []);
  }, [staffInfoData, staffId, dispatch, setStaffNotes]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  if (loading) return <Spinner />;

  const handleSaveChanges = () => {
    if(updateUserInfoLoading) return
    updateStaffInfo()
  }

  return (
    <>
      <Helmet>
        <title>Staff Details - Rook Hotel PMS</title>
        <meta name="description" content="Staff Details - Rook Hotel PMS" />
      </Helmet>
      <SubHeader>
        <div className='title'>
          <Link
            className='highlight' to="/staff"
          >
            Staff
          </Link>
          {`> ` + staffInfo.fullName}  
        </div>
        <div className='buttonContainer'>
          <ModalResetPassword
            buttonLabel="Reset Password"
            buttonStyle={`
              width: 122px;
              margin-left: 14px;
            `}
            email={staffInfo.email}
            phoneNumber={staffInfo.phoneNumber}
            userType='staff'
          />
          <ModalRemoveStaff
            buttonLabel="Remove"
            buttonStyle={`
              width: 78px;
              margin-left: 8px;
            `}
            name={staffInfo.fullName}
            staffId={staffInfo.staffId}
          />
        </div>
      </SubHeader>
      <Content
        additionalstyle={`
          margin-top: 100px;
        `}
      >
        <MobileCommunicationContainer>
          <Button
            mainPage
            onClick={(event)=>{
              if (event.metaKey || event.ctrlKey) {
                // Open new tab with absolute URL
                window.open(`https://pms.rookhotel.com/staff-communications/${staffId}`, '_blank', 'noopener,noreferrer')
              } else {
                // Normal navigation
                navigate(`/staff-communications/${staffId}`)
              }
            }}
            additionalstyle={`

              @media only screen and (min-width: 767px) and (max-width: 1200px) {
                width: 308px;
                height: 55px;
                font-size: 15px;
              }

              @media only screen and (max-width: 767px) {
                width: 100%;
                height: 55px;
                font-size: 15px;
              }
            `}
          >
            <img src={ChatIcon} alt={'chat'} width={29} height={29}/>
            {`Communications with ${staffInfo.fullName}`}
          </Button>
        </MobileCommunicationContainer>
        <ContentColumn>
          <Container>
            <StaffInformation
              staffInfoError={staffInfoError}
              setStaffInfoError={setStaffInfoError}
              btnInfo={
                <>
                  <ModalStaffModifyAccess
                    buttonStyle={`
                      width: 132px;
                    `}
                  />
                  <ModalGenerateKey
                    buttonLabel='Generate Keycard'
                    buttonStyle={`
                      width: 145px;
                      margin-left: 14px;
                    `}
                    roomNumber={'staff'}
                    deviceId={deviceId}
                    setDeviceId={setDeviceId}
                    setKeyPrintModalIsOpen={setKeyPrintModalIsOpen}
                  />
                  <Button
                    mainPage
                    success
                    onClick={handleSaveChanges}
                    data-loading={updateUserInfoLoading}
                    additionalstyle={`
                      margin-left: 14px;
                      width: 113px;
                    `}
                  >
                    {updateUserInfoLoading? null: 'Save Changes'}
                  </Button>
                </>
              }
            />
            <StaffInformationImages />
          </Container>
        </ContentColumn>
        <ContentColumn>
          <Container>
            <GeneralStaffNotes
              notes={staffNotes}
              setNotes={setStaffNotes}
              staffId={staffId}
            />
          </Container>
          <Container>
            <MetaInformation
              created={staffInfoData?.staffByID?.createdAt}
              origin={"Referral"}
            />
          </Container>
        </ContentColumn>
        <DesktopCommunicationContainer>
          <SendStaffMessage />
          <CommunicationHistory />
        </DesktopCommunicationContainer>
      </Content>
      <FooterBar />
    </>
  );
};

export default StaffDetails;
